import { useGridDefinitions } from "@/controls/ag-grid/components/GridDefinitions";
import { CenteredLazyLoading } from "@/controls/LazyLoading";
import { TextFieldSearchFilter } from "@/shared/components/TextFieldSearchFilter";
import { Roll } from "@/shared/models/Roll";
import { ChangeRollStatusModal } from "@/shared/selectors/roll/ChangeRollStatusModal";
import { OptionChip } from "@/shared/selectors/roll/RollFilters";
import { optionsTypeCode } from "@/shared/selectors/RollTypeSelector";
import { ErrorModal, useContextMenuMESF } from "@dexteel/mesf-core";
import { Add } from "@mui/icons-material";
import { Box, Button, Grid2, Paper, Typography } from "@mui/material";
import { useQueryClient } from "@tanstack/react-query";
import {
	DefaultMenuItem,
	GetContextMenuItemsParams,
	GridApi,
	MenuItemDef,
} from "ag-grid-enterprise";
import { AgGridReact } from "ag-grid-react";
import { useEffect, useState } from "react";
import { useBrokenRolls } from "../BrokenRollsPage";
import { useBrokenRollsOptionsFunctions } from "../hooks/useBrokenRollOptionsFunctions";
import { useTableData } from "../hooks/useTableData";
import { AddBrokenRollDialog } from "./AddBrokenRollDialog";

type RollTypeFilter = "U" | "E" | null;

export const TableBrokenRolls = () => {
	const queryClient = useQueryClient();

	const [filterValue, setFilterValue] = useState("");
	const [rollType, setRollType] = useState<RollTypeFilter>(null);
	const [gridApi, setGridApi] = useState<GridApi | null>(null);
	const [error, setError] = useState("");
	const [showStatusModal, setShowStatusModal] = useState(false);
	const [showAddDialog, setShowAddDialog] = useState(false);
	const [selectedRoll, setSelectedRoll] = useState<Roll | null>(null);

	const { data: rows, isLoading, isError, error: e } = useBrokenRolls();

	const openEditModal = (roll: Roll) => {
		setSelectedRoll(roll);
		setShowStatusModal(true);
	};

	const { defaultColDef } = useGridDefinitions({});
	const { columnDefs } = useTableData({ onEdit: openEditModal });

	const { showContextMenu, registerConfig } = useContextMenuMESF();
	const { getMenuOptions } = useBrokenRollsOptionsFunctions({
		onEdit: openEditModal,
	});

	const getContextMenuItems = (
		params: GetContextMenuItemsParams,
	): (DefaultMenuItem | MenuItemDef)[] => {
		const data = params.node?.data;
		showContextMenu(event as any, data, "brokenRollsContext");
		if (data) {
			params.api.deselectAll();
			params.node?.setSelected(true);
		}
		return [];
	};

	const rowsFiltered = rollType
		? rows?.filter((r) => r.RollTypeCode === rollType)
		: rows;

	useEffect(() => {
		if (isError && e) {
			setError(e.message);
		}
	}, [isError, e]);

	useEffect(() => {
		registerConfig({
			id: "brokenRollsContext",
			getOptions: getMenuOptions,
		});
	}, []);

	return (
		<Grid2 container justifyContent="center" p={2} spacing={1}>
			<Grid2 size={{ md: 12, xs: 12 }}>
				<Typography variant="h5" fontWeight={600}>
					Broken Rolls
				</Typography>
			</Grid2>

			<Grid2
				size={{ md: 12, xs: 12 }}
				container
				spacing={2}
				alignItems="center"
			>
				<Grid2 size={{ md: "auto", xs: 12 }}>
					<Box sx={{ display: "flex", gap: 0.75 }}>
						{optionsTypeCode.map((opt) => (
							<OptionChip
								key={opt.Code}
								label={opt.Name}
								selected={rollType === opt.Code}
								onClick={() =>
									setRollType(
										rollType === opt.Code ? null : (opt.Code as "E" | "U"),
									)
								}
							/>
						))}
					</Box>
				</Grid2>

				<Grid2 size={{ md: "grow", xs: 12 }}>
					<TextFieldSearchFilter
						value={filterValue}
						onChange={(val) => {
							setFilterValue(val);
							gridApi?.setGridOption("quickFilterText", val);
						}}
					/>
				</Grid2>

				<Grid2 size={{ md: 1.5, xs: 12 }}>
					<Button
						variant="contained"
						color="primary"
						startIcon={<Add />}
						onClick={() => setShowAddDialog(true)}
						fullWidth
					>
						Add Broken Roll
					</Button>
				</Grid2>
			</Grid2>

			<Grid2 size={{ md: 12, xs: 12 }}>
				<Paper elevation={2} style={{ height: "70vh", width: "100%" }}>
					<AgGridReact<Roll>
						loading={isLoading}
						rowData={rowsFiltered}
						columnDefs={columnDefs}
						defaultColDef={defaultColDef}
						rowHeight={40}
						headerHeight={36}
						animateRows
						getContextMenuItems={getContextMenuItems}
						loadingOverlayComponent={CenteredLazyLoading}
						getRowId={(p) => `${p.data.PartId}`}
						onGridReady={(params) => setGridApi(params.api)}
						gridOptions={{
							rowSelection: {
								mode: "singleRow",
								checkboxes: false,
								enableClickSelection: true,
							},
						}}
					/>
				</Paper>
			</Grid2>

			<ChangeRollStatusModal
				show={showStatusModal}
				roll={selectedRoll}
				onHide={async (updated) => {
					if (updated) {
						await queryClient.invalidateQueries({ queryKey: ["brokenRolls"] });
					}
					setShowStatusModal(false);
					setSelectedRoll(null);
				}}
			/>

			<AddBrokenRollDialog
				open={showAddDialog}
				onClose={() => setShowAddDialog(false)}
			/>

			<ErrorModal error={error} onHide={() => setError("")} />
		</Grid2>
	);
};
